import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { RecordIdLabel } from '../ui/idColumn';
import { useLoad } from '../../hooks/useLoad';
import { useMoney } from '../../hooks/useMoney';
import { useRole } from '../../hooks/useRole';
import { createBookingFromQuote, fetchAcceptedQuotes } from '../../services/VendorBookingRecordService';
import { bookingErrorMessage, existingBookingId } from '../../utils/vendorBookingLabels';
import { formatDecimal } from '../../utils/decimal';
import { entityPath } from '../../utils/entityRoutes';
import { formatDate } from '../../utils/formatDate';
import '../../styles/components/VendorBookings.css';

interface Props {
  onClose: () => void;
  /** Quotes that already have a live booking: they cannot start another. */
  takenQuoteIds: ReadonlySet<string>;
}

/**
 * Starts a booking. A booking cannot exist without a quote, so this is a picker of the project's accepted quotes: choose one
 * and the booking is made from it (vendor, amounts and lines are copied), then its page opens.
 */
const NewBookingModal: React.FC<Props> = ({ onClose, takenQuoteIds }) => {
  const navigate = useNavigate();
  const money = useMoney();
  const { canViewFinancials } = useRole();
  const load = useLoad(fetchAcceptedQuotes, []);
  const [picked, setPicked] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; bookingId: string | null } | null>(null);

  const choices = (load.data ?? []).filter(q => !takenQuoteIds.has(q.id));

  const create = async () => {
    if (!picked || saving) return;
    setSaving(true);
    setError(null);
    try {
      const booking = await createBookingFromQuote(picked);
      onClose();
      navigate(entityPath('vendorBooking', booking.id) ?? '/vendor-bookings');
    } catch (err) {
      setError({ message: bookingErrorMessage(err, 'The booking could not be created. Please try again.'), bookingId: existingBookingId(err) });
    } finally {
      setSaving(false);
    }
  };

  let body: React.ReactNode;
  if (load.error) {
    body = <LoadErrorBox what="accepted quotes" error={load.error} onRetry={load.reload} retrying={load.loading} />;
  } else if (load.loading && !load.data) {
    body = <p className="vb-note" role="status">Loading accepted quotes...</p>;
  } else if (choices.length === 0) {
    body = (
      <div role="status">
        <p className="vb-note">There is no accepted quote waiting for a booking. A booking starts from a quote the vendor has agreed to.</p>
        <p className="vb-note"><Link className="vb-link" to="/quotes" onClick={onClose}>Open the quotes</Link> to accept one.</p>
      </div>
    );
  } else {
    body = (
      <fieldset className="vb-picker">
        <legend className="vb-note">Choose the accepted quote this booking is for.</legend>
        {choices.map(q => (
          <label key={q.id} className={`vb-picker__option${picked === q.id ? ' vb-picker__option--on' : ''}`}>
            <input type="radio" name="quote" value={q.id} checked={picked === q.id} onChange={() => setPicked(q.id)} />
            <span className="vb-picker__body">
              <strong>{q.vendor_name || 'Vendor'}</strong>
              <span className="vb-picker__meta">
                <RecordIdLabel kind="quote" id={q.id} />
                {q.date_received ? ` · received ${formatDate(q.date_received, '')}` : ''}
                {canViewFinancials ? ` · ${formatDecimal(q.total_amount, q.currency || money.currency)}` : ''}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New booking"
      size="medium"
      footer={(
        <div className="modal-actions">
          <Button variant="primary" loading={saving} disabled={!picked} onClick={() => void create()}>Create booking</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      )}
    >
      {body}
      {error && (
        <p className="form-error-text" role="alert">
          {error.message}{' '}
          {error.bookingId && <Link className="vb-link" to={entityPath('vendorBooking', error.bookingId) ?? '/vendor-bookings'} onClick={onClose}>Open that booking</Link>}
        </p>
      )}
    </Modal>
  );
};

export default NewBookingModal;
